import { localGeodata } from './geodata.js';

/**
 * Registering the vendored renderers, in dependency order.
 *
 * The four map modules are custom elements vendored verbatim (M5-D7). None of
 * them is a plain import: each wants globals in place first, and `mw-map.js`
 * wants another element registered first. Both are silent failures, which is
 * exactly the kind of thing that belongs behind a named function rather than in
 * an import list.
 *
 * ## `region-map.js` waits on three globals and fetches two files
 *
 * `depsReady()` polls for `window.d3` and `window.topojson` and never times out,
 * so a missing global is a map that stays blank with nothing in the console.
 * `msNode` reads `window.ms` and falls back to hand-drawn glyphs without it —
 * which would put a *different* symbol vocabulary on the canvas from the one the
 * rails draw (`chrome/MilSymbol.tsx`), for no reason other than a forgotten
 * assignment.
 *
 * All three come from the same npm packages the rest of the console uses, so the
 * canvas and the rails cannot disagree about what a symbol looks like. They are
 * dynamically imported here rather than at module scope: d3 is ~250 kB and no
 * seat that never opens a map tab should pay for it.
 *
 * The three geodata urls are answered from the bundle — see `maps/geodata.ts` for
 * why that is done by handing the renderer a `d3` whose `json` knows them, and
 * not by editing the file.
 *
 * ## `orbital-map.js` stands on its own
 *
 * It `extends HTMLElement`, so unlike `mw-map.js` nothing has to be registered
 * before it and there is no chain to get wrong. It wants the same three globals
 * and it fetches a third url (`countries-110m.json`, for the globe at the centre
 * of the board), which is why the globals are a function both entry points await
 * rather than a block inside `ensureRegionMap`: a seat that opens SPACE · SDA
 * and never opens REGION MAP must not depend on the theatre's boot for its d3.
 *
 * ## `cyber-map.js` stands on its own too, and wants no geodata at all
 *
 * It `extends HTMLElement` and imports `./scenario.js` for the fourteen cyber
 * members `model/cyber.ts` builds — no d3 projection, no topojson, no atlas, and
 * no basemap. It still goes through `ensureGlobals()` rather than importing
 * bare, for a reason that is about *this* function rather than about the file:
 * the three globals are what every renderer here has in place before it
 * evaluates, and an entry point that skipped them would be the one place a
 * future edit to the vendored file could reach for `window.d3` and find nothing.
 * The cost is one shared dynamic import a CYBER seat was already going to pay
 * the moment it opened THEATRE.
 *
 * ## `mw-map.js` is not a standalone element
 *
 * Its first three statements are
 *
 * ```js
 * const Base = customElements.get('region-map');
 * if (!Base) return;
 * class MwMap extends Base { … }
 * ```
 *
 * so it **defines nothing at all** unless the theatre map is already registered,
 * and evaluating it too early is not an error that anything reports — the module
 * simply returns and `mw-map` stays an unknown tag forever. Phase 4 shipped this
 * function returning early for exactly that reason; Phase 5 registers the base
 * first, and the MW board's map lights up with no change to `mw/MwView.tsx` or
 * to either vendored file.
 */

/**
 * The three libraries the renderers reach for by global name.
 *
 * Written through a local shape rather than a `Window` augmentation: `@types/d3`
 * already declares `d3` as a UMD global, and re-declaring it here as `unknown`
 * would have this file arguing with the type definitions over a value neither of
 * us reads back.
 */
interface MapGlobals {
  /** The renderer's `depsReady()` polls for these two, forever. */
  d3: unknown;
  topojson: unknown;
  /** milsymbol. Absent means hand-drawn fallback glyphs, never an error. */
  ms: unknown;
}

declare global {
  interface Window {
    /** Terrain vs vector basemap. Seeded by the wrapper, written by the map's own TER/VEC button. */
    BS_MAP_TERRAIN?: boolean;
    /** The scenario facade. `region-map.js` reads it directly in `fitSide()`. */
    BS_SCENARIO?: unknown;
    /** The scenario adapter's shared cell — `vendor/maps/scenario.js` owns it. */
    __BS_SCENARIO_CELL?: unknown;
  }
}

let globalsPending: Promise<void> | null = null;
let regionPending: Promise<void> | null = null;
let orbitalPending: Promise<void> | null = null;
let cyberPending: Promise<void> | null = null;
let mwPending: Promise<void> | null = null;

function registered(tag: string): boolean {
  return typeof customElements !== 'undefined' && customElements.get(tag) !== undefined;
}

/** True once `<region-map>` is defined and can be mounted. */
export function regionMapRegistered(): boolean {
  return registered('region-map');
}

/** True once `<orbital-map>` is defined and the SDA board can be mounted. */
export function orbitalMapRegistered(): boolean {
  return registered('orbital-map');
}

/** True once `<cyber-map>` is defined and the cyber board can be mounted. */
export function cyberMapRegistered(): boolean {
  return registered('cyber-map');
}

/** True once `<mw-map>` is defined. False until the theatre map has registered. */
export function mwMapRegistered(): boolean {
  return registered('mw-map');
}

/**
 * d3, with the renderer's two geodata urls answered locally.
 *
 * A shallow copy rather than a mutation of the module namespace, which is
 * frozen — and a copy is the honest shape anyway: this is *our* d3 for *this*
 * consumer, not a global patch that would follow d3 into every other caller.
 */
function withLocalGeodata(d3: Record<string, unknown>): Record<string, unknown> {
  const json = d3['json'] as (url: string, init?: RequestInit) => Promise<unknown>;
  return {
    ...d3,
    json: (url: string, init?: RequestInit): Promise<unknown> =>
      localGeodata(url) ?? json(url, init),
  };
}

/**
 * Evaluate `vendor/maps/region-map.js`, once, with its globals in place.
 *
 * Memoised on the promise rather than on a boolean: two panes mounting in the
 * same frame would otherwise both start the import, and a custom element defined
 * twice throws.
 */
function ensureGlobals(): Promise<void> {
  globalsPending ??= (async (): Promise<void> => {
    const [d3, topojson, milsymbol] = await Promise.all([
      import('d3'),
      import('topojson-client'),
      import('milsymbol'),
    ]);
    const globals = window as unknown as MapGlobals;
    globals.d3 = withLocalGeodata(d3 as unknown as Record<string, unknown>);
    globals.topojson = topojson;
    globals.ms = (milsymbol as { default?: unknown }).default ?? milsymbol;
  })();
  return globalsPending;
}

export function ensureRegionMap(): Promise<void> {
  regionPending ??= ensureGlobals().then(async () => {
    await import('../../../vendor/maps/region-map.js');
  });
  return regionPending;
}

/**
 * Evaluate `vendor/maps/orbital-map.js`, once, with its globals in place.
 *
 * No chain: it extends `HTMLElement` and defines itself. What it does share with
 * the theatre map is `./scenario.js`, which it imports by the same path and
 * therefore reads the same adopted model out of (`maps/useScenarioFacade.ts`) —
 * so a console that has opened either board has the facade for both.
 */
export function ensureOrbitalMap(): Promise<void> {
  orbitalPending ??= ensureGlobals().then(async () => {
    await import('../../../vendor/maps/orbital-map.js');
  });
  return orbitalPending;
}

/**
 * Evaluate `vendor/maps/cyber-map.js`, once, with its globals in place.
 *
 * No chain, the same as the orbital board: `<cyber-map>` extends `HTMLElement`
 * and defines itself. **Check this first if you are adding a fifth renderer** —
 * two of the four stand alone and one does not, and `mw-map.js`'s failure mode
 * is silence rather than an error.
 */
export function ensureCyberMap(): Promise<void> {
  cyberPending ??= ensureGlobals().then(async () => {
    await import('../../../vendor/maps/cyber-map.js');
  });
  return cyberPending;
}

/**
 * Evaluate `vendor/maps/mw-map.js`, once, after the element it extends.
 *
 * The chain is the whole point: an operator who opens MISSILE WARNING without
 * ever opening REGION MAP would otherwise get a permanently mapless board, and
 * nothing anywhere would say why.
 */
export function ensureMwMap(): Promise<void> {
  mwPending ??= ensureRegionMap().then(async () => {
    await import('../../../vendor/maps/mw-map.js');
  });
  return mwPending;
}
